import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Inbox,
  CheckCircle2,
  Circle,
  Trash2,
  RefreshCw,
  Mail,
  Phone,
  Building2,
  Clock,
} from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLeads = ({ token }) => {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [untriagedOnly, setUntriagedOnly] = useState(true);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchSubs = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/contact/submissions?limit=200`, { headers });
      setSubs(data.submissions || []);
      setTotal(data.total || 0);
      setUnread(data.unread || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchSubs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const toggleTriage = async (s) => {
    try {
      await axios.put(
        `${API}/contact/submissions/${s.id}/triage`,
        {},
        { headers }
      );
      toast.success(s.triaged ? 'Marked as untriaged' : 'Marked as triaged');
      fetchSubs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  const deleteSub = async (s) => {
    if (!window.confirm(`Delete lead from ${s.name}? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/contact/submissions/${s.id}`, { headers });
      toast.success('Lead deleted');
      fetchSubs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const filtered = untriagedOnly ? subs.filter((s) => !s.triaged) : subs;
  const untriagedCount = subs.filter((s) => !s.triaged).length;

  const fmtDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-leads-panel">
      {/* Toolbar */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-sm">
          <Inbox className="w-4 h-4 text-gold-500" />
          <span className="font-medium text-navy-900">
            {untriagedCount} untriaged
          </span>
          <span className="text-gray-400">/ {total} total</span>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
          <input
            type="checkbox"
            checked={untriagedOnly}
            onChange={(e) => setUntriagedOnly(e.target.checked)}
            className="rounded border-gray-300"
            data-testid="untriaged-only-toggle"
          />
          Show untriaged only
        </label>
        <div className="flex-1" />
        <Button
          size="sm"
          variant="outline"
          onClick={fetchSubs}
          className="h-8 text-xs"
          data-testid="refresh-leads"
        >
          <RefreshCw className="w-3.5 h-3.5 mr-1" />
          Refresh
        </Button>
      </div>

      {/* Lead list */}
      {loading ? (
        <div className="p-8 text-center text-sm text-gray-500 bg-white border border-gray-200 rounded-lg">
          Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="p-10 text-center bg-white border border-gray-200 rounded-lg">
          <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-2" />
          <p className="text-sm font-medium text-navy-900">
            {untriagedOnly ? 'All leads triaged. Nice work.' : 'No leads yet.'}
          </p>
          {untriagedOnly && subs.length > 0 && (
            <button
              type="button"
              onClick={() => setUntriagedOnly(false)}
              className="mt-2 text-xs text-gold-600 hover:text-gold-700 underline"
            >
              Show all {subs.length} leads
            </button>
          )}
        </div>
      ) : (
        <ul className="space-y-3">
          {filtered.map((s) => (
            <li
              key={s.id}
              className={`bg-white border rounded-lg p-4 ${
                s.triaged ? 'border-gray-200 opacity-75' : 'border-gold-300 shadow-sm'
              }`}
              data-testid={`lead-row-${s.id}`}
            >
              <div className="flex flex-wrap items-start gap-3">
                <div className="flex-1 min-w-[260px]">
                  <div className="flex flex-wrap items-center gap-2 mb-1.5">
                    <span className="font-semibold text-navy-900">{s.name}</span>
                    {s.triaged ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-green-100 text-green-700 border border-green-200">
                        <CheckCircle2 className="w-3 h-3" />
                        Triaged
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-gold-100 text-gold-800 border border-gold-300">
                        <Circle className="w-3 h-3" />
                        New
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 mb-2">
                    <a
                      href={`mailto:${s.email}`}
                      className="inline-flex items-center gap-1 hover:text-navy-900"
                    >
                      <Mail className="w-3 h-3" />
                      {s.email}
                    </a>
                    {s.phone && (
                      <span className="inline-flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {s.phone}
                      </span>
                    )}
                    {s.company && (
                      <span className="inline-flex items-center gap-1">
                        <Building2 className="w-3 h-3" />
                        {s.company}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {fmtDate(s.created_at)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {s.message}
                  </p>
                  {s.triaged && s.triaged_at && (
                    <p className="mt-2 text-[11px] text-gray-400 italic">
                      Triaged {fmtDate(s.triaged_at)}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-2 min-w-[130px]">
                  <Button
                    size="sm"
                    onClick={() => toggleTriage(s)}
                    className={`h-8 text-xs ${
                      s.triaged
                        ? 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300'
                        : 'bg-navy-900 hover:bg-navy-800 text-white'
                    }`}
                    variant={s.triaged ? 'outline' : 'default'}
                    data-testid={`triage-btn-${s.id}`}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                    {s.triaged ? 'Mark untriaged' : 'Mark triaged'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => deleteSub(s)}
                    className="h-8 text-xs text-red-600 hover:bg-red-50 hover:text-red-700 border-red-200"
                    data-testid={`delete-lead-${s.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <p className="text-xs text-gray-500 leading-relaxed">
        Leads are also triaged automatically when someone clicks the "Mark as
        triaged" link in the notification email — that flag will show up here on
        the next refresh.
      </p>
    </div>
  );
};

export default AdminLeads;
